import styled from 'styled-components';
import {
  Flex,
  FlexColumn,
  IconButton,
  RadioGroup,
  LabeledRadioFormField,
  Chip,
  FormField,
} from '@elementary/common-ui/components';
import {
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ReactComponent as TrashIcon } from '@elementary/common-ui/assets/20x20/trash.svg';
import { useFormContext } from 'react-hook-form';
import { WarehouseType } from '@elementary/common-ui/types';
import { AssetSourceFormData, EnvironmentForm } from 'shared/types';
import { fontWeight, radius, space } from '@elementary/common-ui/style/utils';
import { DBT_PACKAGE_DOCS_URL } from './constants';
import DbtProjectFields from './DbtProjectFields';
import WarehouseSourceFields from './WarehouseSourceFields';

const DETAILS_PADDING = '11px 15px 24px';

type Props = {
  index: number;
  source: AssetSourceFormData;
  warehouseType: WarehouseType;
  isExisting: boolean;
  onRemove: () => void;
  onKindChange: (kind: AssetSourceFormData['type']) => void;
  canRemove: boolean;
  disabled?: boolean;
  isFirst?: boolean;
  expanded: boolean;
  onToggle: () => void;
};

const StyledAccordion = styled(Accordion)<{ $isFirst?: boolean }>`
  && {
    box-shadow: none;
    border: 1px solid ${({ theme }) => theme.palette.border.default};
    border-radius: ${({ $isFirst }) =>
      $isFirst ? radius('md', 'md', 0, 0) : '0'};
    margin-top: ${({ $isFirst }) => ($isFirst ? '0' : '-1px')};

    &::before {
      display: none;
    }
  }
`;

const StyledAccordionSummary = styled(AccordionSummary)<{ $isFirst?: boolean }>`
  && {
    height: 56px;
    padding: ${space(0, 'md')};
    background-color: ${({ theme }) => theme.palette.surface.base};
    border-radius: ${({ $isFirst }) =>
      $isFirst ? radius('md', 'md', 0, 0) : '0'};

    .MuiAccordionSummary-content {
      display: flex;
      align-items: center;
      padding-left: ${space('sm')};
    }

    .MuiAccordionSummary-expandIconWrapper {
      order: -1;
      color: ${({ theme }) => theme.palette.content.primary};
    }
  }
`;

const StyledAccordionDetails = styled(AccordionDetails)`
  && {
    padding: ${DETAILS_PADDING};
    background-color: ${({ theme }) => theme.palette.surface.soft};
    border-top: 1px solid ${({ theme }) => theme.palette.border.default};
  }
`;

const TitleContainer = styled(Flex)`
  display: flex;
  align-items: center;
  gap: ${space('sm')};
  flex: 1;
`;

const SourceTitle = styled(Typography)<{ $isEmpty?: boolean }>`
  font-weight: ${({ theme }) => theme.typography.fontWeightMedium};
  color: ${({ theme, $isEmpty }) =>
    $isEmpty ? theme.palette.content.disabled : theme.palette.content.muted};
`;

const ErrorDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: ${radius('round')};
  background-color: ${({ theme }) => theme.palette.status.error.main};
  flex-shrink: 0;
`;

const RemoveButton = styled(IconButton)`
  && {
    padding: ${space('xs')};
    svg {
      width: 20px;
      height: 20px;
      color: ${({ theme }) => theme.palette.content.primary};
    }

    &:hover {
      svg {
        color: ${({ theme }) => theme.palette.status.error.main};
      }
    }
  }
`;

const FieldsContainer = styled(FlexColumn)`
  gap: ${space('lg')};
`;

const kindOptions: {
  value: AssetSourceFormData['type'];
  chipLabel: string;
  label: string;
}[] = [
  { value: 'dbt', chipLabel: 'dbt', label: 'dbt project' },
  { value: 'warehouse', chipLabel: 'Warehouse', label: 'Warehouse schemas' },
];

const AssetSourceAccordion = ({
  index,
  source,
  warehouseType,
  isExisting,
  onRemove,
  onKindChange,
  canRemove,
  disabled,
  isFirst,
  expanded,
  onToggle,
}: Props) => {
  const {
    formState: { errors },
  } = useFormContext<EnvironmentForm>();

  const hasErrors = errors.assetSources?.[index] != null;
  const displayName = source.name?.trim() || 'Untitled asset source';
  const isEmpty = !source.name?.trim();
  const kind = kindOptions.find((option) => option.value === source.type);

  const handleRemoveClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRemove();
  };

  return (
    <StyledAccordion
      $isFirst={isFirst}
      expanded={expanded}
      onChange={onToggle}
      disableGutters
    >
      <StyledAccordionSummary
        $isFirst={isFirst}
        expandIcon={<ExpandMoreIcon />}
      >
        <TitleContainer>
          <SourceTitle $isEmpty={isEmpty}>{displayName}</SourceTitle>
          {kind && (
            <Chip label={kind.chipLabel} size="small" overflow="visible" />
          )}
          {hasErrors && <ErrorDot />}
        </TitleContainer>
        {canRemove && (
          <RemoveButton onClick={handleRemoveClick} disabled={disabled}>
            <TrashIcon />
          </RemoveButton>
        )}
      </StyledAccordionSummary>
      <StyledAccordionDetails>
        <FieldsContainer>
          {!isExisting && (
            <FormField title="Asset source type">
              <RadioGroup
                sx={{ gap: 2 }}
                value={source.type}
                onChange={(_, value) => {
                  if (value === 'dbt' || value === 'warehouse') {
                    onKindChange(value);
                  }
                }}
              >
                {kindOptions.map((option) => (
                  <LabeledRadioFormField
                    key={option.value}
                    value={option.value}
                    selected={source.type === option.value}
                    disabled={disabled}
                    label={
                      <FlexColumn>
                        <Typography sx={{ fontWeight: fontWeight('medium') }}>
                          {option.label}
                        </Typography>
                        <Typography sx={{ color: 'content.muted' }}>
                          {option.value === 'dbt' ? (
                            <>
                              Uses dbt models, tests and run results. The{' '}
                              <Link
                                href={DBT_PACKAGE_DOCS_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(event) => event.stopPropagation()}
                              >
                                Elementary dbt package
                              </Link>{' '}
                              is required.
                            </>
                          ) : (
                            'Monitors tables directly in your warehouse.'
                          )}
                        </Typography>
                      </FlexColumn>
                    }
                  />
                ))}
              </RadioGroup>
            </FormField>
          )}
          {source.type === 'dbt' ? (
            <DbtProjectFields
              index={index}
              warehouseType={warehouseType}
              isExisting={isExisting}
              disabled={disabled}
            />
          ) : (
            <WarehouseSourceFields
              index={index}
              warehouseType={warehouseType}
              isNameRequired={!isExisting}
              disabled={disabled}
            />
          )}
        </FieldsContainer>
      </StyledAccordionDetails>
    </StyledAccordion>
  );
};

export default AssetSourceAccordion;
